import type { ReadinessResult } from "./cftcRulesEngine";

export type PriceReferenceMode = "anchored" | "live";

export const computeDaysSinceCftcAsOf = (
  asOfDate: string | null | undefined,
  now: number | Date = Date.now()
) => {
  if (!asOfDate) {
    return null;
  }
  const asOf = new Date(asOfDate);
  const nowDate = typeof now === "number" ? new Date(now) : now;
  if (Number.isNaN(asOf.getTime()) || Number.isNaN(nowDate.getTime())) {
    return null;
  }
  const diffMs = nowDate.getTime() - asOf.getTime();
  if (!Number.isFinite(diffMs)) {
    return null;
  }
  return Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000)));
};

export const computeLiveDecay = (daysSinceCftc: number) => {
  const rawDecay = 1 - (daysSinceCftc / 10) * 0.25;
  return Math.max(0.7, Math.min(1, rawDecay));
};

export const selectPriceAsOfDate = (
  mode: PriceReferenceMode,
  anchoredDate: string | null | undefined,
  liveDate: string | null | undefined
) => {
  if (mode === "live" && liveDate) {
    return liveDate;
  }
  return anchoredDate ?? null;
};

export const applyLiveDecayToReadiness = (
  readiness: ReadinessResult,
  decay: number,
  range: [number, number],
  labels: Array<{ min: number; max: number; label: string }>
) => {
  const nextComponents = readiness.components.map((component) => {
    if (component.name !== "trigger" && component.name !== "pressure") {
      return component;
    }
    const nextScore = Math.max(0, Math.min(component.score * decay, component.max));
    return {
      ...component,
      score: nextScore,
    };
  });
  const rawScore = nextComponents.reduce((sum, item) => sum + item.score, 0);
  const [minScore, maxScore] = range;
  const score = Math.max(minScore, Math.min(rawScore, maxScore));
  const labelMatch = labels.find((entry) => score >= entry.min && score <= entry.max);
  return {
    ...readiness,
    score,
    label: labelMatch ? labelMatch.label : readiness.label,
    components: nextComponents,
  };
};
